import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { Dialog, Bar, Label, Switch, AnalyticalTable } from "@ui5/webcomponents-react";
import { Card, CardHeader, Input } from "@ui5/webcomponents-react";
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from "@mui/icons-material/Add";
import ButtonContained from "../StyledComponents/ButtonContained";
import ButtonOutlined from "../StyledComponents/ButtonOutlined";
import httpRequest from "../../httpRequest";
import { Delete, Edit } from "@mui/icons-material";

const FunctionsCard = () => {
  const [functions, setFunctions] = useState([]);
  const [newFunctionName, setNewFunctionName] = useState("");
  const [newFunctionExpense, setNewFunctionExpense] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editFunctionId, setEditFunctionId] = useState(null);
  const [editFunctionName, setEditFunctionName] = useState("");
  const [editFunctionExpense, setEditFunctionExpense] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [functionToDelete, setFunctionToDelete] = useState(null);
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    fetchFunctions();
  }, []);

  const fetchFunctions = async () => {
    setLoading(true);
    try {
      const resp = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/functions`);
      setFunctions(resp.data.functions);
    } catch (error) {
      console.error("Error fetching functions:", error);
    } finally {
      setLoading(false);
    }
  };


  const handleAddFunction = async () => {
    try {
      const response = await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/add-function`, {
        function_name: newFunctionName,
        monthly_expense: newFunctionExpense,
      });
      if (response.status === 201) {
        enqueueSnackbar("Function added successfully", { variant: "success" });
        fetchFunctions();
        setNewFunctionName("");
        setNewFunctionExpense(false);
        setAddDialogOpen(false);
      } else {
        enqueueSnackbar("Failed to add function", { variant: "error" });
      }
    } catch (error) {
      console.error("Error adding function:", error);
      enqueueSnackbar("Failed to add function", { variant: "error" });
    }
  };

  const handleDeleteFunction = (functionId) => {
    setFunctionToDelete(functionId);
    setDeleteConfirmationOpen(true);
  };

  const confirmDeleteFunction = async () => {
    try {
      await httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/delete-function/${functionToDelete}`);
      fetchFunctions();
      enqueueSnackbar("Function deleted successfully", { variant: "success" });
    } catch (error) {
      console.error("Error deleting function:", error);
      enqueueSnackbar("Failed to delete function", { variant: "error" });
    } finally {
      setDeleteConfirmationOpen(false);
    }
  };

  const cancelDeleteFunction = () => {
    setFunctionToDelete(null);
    setDeleteConfirmationOpen(false);
  };

  const handleOpenEditDialog = (functionId, functionName, expense) => {
    setEditFunctionId(functionId);
    setEditFunctionName(functionName);
    setEditFunctionExpense(expense);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditFunctionId(null);
    setEditFunctionName("");
    setEditFunctionExpense(false);
    setEditDialogOpen(false);
  };

  const handleOpenAddDialog = () => {
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setNewFunctionName("");
    setNewFunctionExpense(false);
    setAddDialogOpen(false);
  };

  const handleUpdateFunction = async () => {
    try {
      const response = await httpRequest.put(`${process.env.REACT_APP_BACKEND_URL}/update-function/${editFunctionId}`, {
        function_name: editFunctionName,
        monthly_expense: editFunctionExpense,
      });
      if (response.status === 200) {
        enqueueSnackbar("Function updated successfully", { variant: "success" });
        fetchFunctions();
        handleCloseEditDialog();
      } else {
        enqueueSnackbar("Failed to update function", { variant: "error" });
      }
    } catch (error) {
      console.error("Error updating function:", error);
      enqueueSnackbar("Failed to update function", { variant: "error" });
    }
  };

  const columns = [
    { accessor: 'function_name', Header: 'Function Name' },
    {
      accessor: 'monthly_expense',
      Header: 'Monthly Expense', autoResizable: true,
      Cell: ({ value }) => (value ? 'Yes' : 'No'),
    },
    {
      Header: 'Actions',
      Cell: ({ row }) => (
        <>
          <ButtonContained onClick={() => handleOpenEditDialog(row.original.id, row.original.function_name, row.original.monthly_expense)} style={{ marginRight: 10 }}>
            <Edit />
          </ButtonContained>
          <ButtonOutlined onClick={() => handleDeleteFunction(row.original.id)}>
            <Delete />
          </ButtonOutlined>
        </>
      ),
    },
  ];

  const filteredFunctions = functions.filter((func) =>
    func.function_name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  return (
    <>
      <Card header={<CardHeader subtitleText="Create and manage functions" titleText="Function Management" />}>
        <div style={{ padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <input
            type="text"
            placeholder="Search Functions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: "1", padding: "10px", marginRight: "20px" }}
          />
          <ButtonContained variant="contained" color="primary" onClick={handleOpenAddDialog}>
            <AddIcon /> Add Function
          </ButtonContained>
        </div>
        <AnalyticalTable
            data={filteredFunctions}
            columns={columns}
            sortable
            visibleRows={8}
            scaleWidthMode="Smart"
            loading={loading}
            style={{ paddingLeft: '20px', paddingRight: '20px', paddingBottom: '20px' }}
          />
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        headerText="Confirm Delete"
        open={deleteConfirmationOpen}
        onAfterClose={cancelDeleteFunction}
        footer={
          <Bar design="Footer" endContent={
            <>
              <ButtonOutlined style={{ marginRight: 8 }} onClick={cancelDeleteFunction}>
                Cancel
              </ButtonOutlined>
              <ButtonContained onClick={confirmDeleteFunction}>
                Delete
              </ButtonContained>
            </>
          } />
        }
      >
        Are you sure you want to delete this function?
      </Dialog>

      {/* Edit Function Dialog */}
      <Dialog
        headerText="Edit Function"
        open={editDialogOpen}
        onAfterClose={handleCloseEditDialog}
        footer={
          <Bar design="Footer" endContent={
            <>
              <ButtonOutlined style={{ marginRight: 8 }} onClick={handleCloseEditDialog}>
                Cancel
              </ButtonOutlined>
              <ButtonContained onClick={handleUpdateFunction}>
                Update
              </ButtonContained>
            </>
          } />
        }
      >
        <Label>Function Name</Label>
        <Input
          placeholder="Enter name"
          value={editFunctionName}
          onChange={(e) => setEditFunctionName(e.target.value)}
          showSuggestions={false}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
        <Label style={{ flex: '1' }}>Monthly Expense</Label>
        <Switch
          checked={editFunctionExpense}
          onChange={(e) => setEditFunctionExpense(e.target.checked)}
          style={{ marginLeft: 'auto' }}
        />
        </div>
      </Dialog>

      {/* Add Function Dialog */}
      <Dialog
        headerText="Add Function"
        open={addDialogOpen}
        onAfterClose={handleCloseAddDialog}
        footer={
          <Bar design="Footer" endContent={
            <>
              <ButtonOutlined style={{ marginRight: 8 }} onClick={handleCloseAddDialog}>
                Cancel
              </ButtonOutlined>
              <ButtonContained onClick={handleAddFunction}>
                Add
              </ButtonContained>
            </>
          } />
        }
      >
        <Label>Function Name</Label>
        <Input
          placeholder="New function name"
          value={newFunctionName}
          onChange={(e) => setNewFunctionName(e.target.value)}
          showSuggestions={false}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
  <Label style={{ flex: '1' }}>Monthly Expense</Label>
  <Switch
    checked={newFunctionExpense}
    onChange={(e) => setNewFunctionExpense(e.target.checked)}
    style={{ marginLeft: 'auto' }}
  />
</div>

      </Dialog>
    </>
  );
};

export default FunctionsCard;
